import moment from "moment";
import React from "react";
import { CurrentFinanceYear } from "../../../../config/app-constants";
import { UtilService } from "../../../../services";

import { useFormikContext } from "formik";
import { StockRSUTypesOptions } from "../../../../config/filingConstants";
import { IStockOptionsRSUsForm } from "../../../../config/interfaces";
import {
  DateTimePicker,
  FormField,
  NumericInputField,
  SelectInputField,
  TextInputField
} from "../../../fields";

type Props = {};

const CGStockOptionsRSUsForm: React.FC<Props> = (props: Props) => {
  const {} = props;

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IStockOptionsRSUsForm>();

  const getNetIncome = async (
    TotalSalePrice: string | 0,
    FairMarketValue: string | 0,
    ExerciseValue: string | 0,
    TransferPrice: string | 0
  ) => {
    let gainsValue;
    if (Number(TotalSalePrice) > Number(FairMarketValue)) {
      gainsValue =
        Number(TotalSalePrice || 0) - Number(ExerciseValue || 0) - Number(TransferPrice || 0);
    } else {
      gainsValue =
        Number(FairMarketValue || 0) - Number(ExerciseValue || 0) - Number(TransferPrice || 0);
    }
    setFieldValue("Gains", gainsValue);
  };

  return (
    <>
      <section className="mb-5">
        <FormField label="Type of Stock option/RSU" isRequired={true} sm={4}>
          <SelectInputField
            placeholder="Type of Stock option/RSU"
            name="TypeOfStock"
            isOptionArrayOfObject={false}
            options={StockRSUTypesOptions}
            value={[values.TypeOfStock]}
            onChange={(_selectedTypesAssets) => {
              if (_selectedTypesAssets) {
                setFieldValue("TypeOfStock", _selectedTypesAssets.value);
              } else {
                setFieldValue("TypeOfStock", undefined);
              }
            }}
            onBlur={handleBlur("TypeOfStock")}
            isInvalid={Boolean(errors.TypeOfStock) && touched.TypeOfStock}
            errorMsg={errors.TypeOfStock}
          />
        </FormField>

        <FormField label="Date of Sale" isRequired={true} sm={4}>
          <DateTimePicker
            name="SaleDate"
            placeholder="DD/MM/YYYY"
            value={values.SaleDate ? moment(values.SaleDate).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("SaleDate", _date);
              } else {
                setFieldValue("SaleDate", "");
              }
            }}
            onBlur={handleBlur("SaleDate")}
            minDate={
              values?.ExerciseDate
                ? moment(values.ExerciseDate).toDate()
                : moment(CurrentFinanceYear).toDate()
            }
            maxDate={moment(CurrentFinanceYear).toDate()}
            isInvalid={Boolean(errors.SaleDate) && touched.SaleDate}
            errorMsg={errors.SaleDate}
            isClearable={true}
          />
        </FormField>

        <FormField label="Date of Exercise" isRequired={true} sm={4}>
          <DateTimePicker
            name="ExerciseDate"
            placeholder="DD/MM/YYYY"
            value={values.ExerciseDate ? moment(values.ExerciseDate).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("ExerciseDate", _date);
              } else {
                setFieldValue("ExerciseDate", "");
              }
            }}
            onBlur={handleBlur("ExerciseDate")}
            minDate={undefined}
            maxDate={
              values?.ExerciseDate
                ? moment(values.ExerciseDate).toDate()
                : moment(CurrentFinanceYear).toDate()
            }
            isInvalid={Boolean(errors.ExerciseDate) && touched.ExerciseDate}
            errorMsg={errors.ExerciseDate}
            isClearable={true}
          />
        </FormField>

        {values.TypeOfStock === "Indian ESOP&RSU - Listed" ||
        values.TypeOfStock === "Indian ESOP&RSU - Unlisted" ? (
          <FormField label="Description of Sale" sm={4}>
            <TextInputField
              name="DescriptionOfSale"
              value={values.DescriptionOfSale}
              onChange={(value) => {
                setFieldValue("DescriptionOfSale", value);
              }}
              onBlur={(e) => {
                handleBlur(e);
                setFieldValue("DescriptionOfSale", values.DescriptionOfSale.trim());
              }}
              isInvalid={Boolean(errors.DescriptionOfSale) && touched.DescriptionOfSale}
              errorMsg={errors.DescriptionOfSale}
            />
          </FormField>
        ) : null}

        <FormField label="Total Sale Price" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="TotalSalePrice"
            value={values.TotalSalePrice}
            onChange={(value) => {
              setFieldValue("TotalSalePrice", value);
              getNetIncome(
                value,
                values.FairMarketValue,
                values.ExerciseValue,
                values.TransferPrice
              );
            }}
            onBlur={handleBlur("TotalSalePrice")}
            isInvalid={Boolean(errors.TotalSalePrice) && touched.TotalSalePrice}
            errorMsg={errors.TotalSalePrice}
          />
        </FormField>

        <FormField label="Transfer Price" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="TransferPrice"
            value={values.TransferPrice}
            onChange={(value) => {
              setFieldValue("TransferPrice", value);
              getNetIncome(
                values.TotalSalePrice,
                values.FairMarketValue,
                values.ExerciseValue,
                value
              );
            }}
            onBlur={handleBlur("TransferPrice")}
            isInvalid={Boolean(errors.TransferPrice) && touched.TransferPrice}
            errorMsg={errors.TransferPrice}
          />
        </FormField>

        <FormField label="Value as on date of exercise" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="ExerciseValue"
            value={values.ExerciseValue}
            onChange={(value) => {
              setFieldValue("ExerciseValue", value);
              getNetIncome(
                values.TotalSalePrice,
                values.FairMarketValue,
                value,
                values.TransferPrice
              );
            }}
            onBlur={handleBlur("ExerciseValue")}
            isInvalid={Boolean(errors.ExerciseValue) && touched.ExerciseValue}
            errorMsg={errors.ExerciseValue}
          />
        </FormField>

        <FormField label="Did you pay STT" sm={4}>
          <SelectInputField
            placeholder="Pay STT"
            name="PaySTT"
            isOptionArrayOfObject={false}
            options={["Yes", "No"]}
            value={[values.PaySTT]}
            onChange={(_STTPaid) => {
              if (_STTPaid) {
                setFieldValue("PaySTT", _STTPaid.value);
              } else {
                setFieldValue("PaySTT", undefined);
              }
            }}
            onBlur={handleBlur("PaySTT")}
            isInvalid={Boolean(errors.PaySTT) && touched.PaySTT}
            errorMsg={errors.PaySTT}
          />
        </FormField>

        {values.TypeOfStock === "Foreign RSU&ESOP - Listed" ||
        values.TypeOfStock === "Foreign RSU&ESOP - Unlisted" ? (
          <FormField label="Description of asset sold" sm={4}>
            <TextInputField
              name="DescriptionAssetSold"
              value={values.DescriptionAssetSold}
              onChange={(value) => {
                setFieldValue("DescriptionAssetSold", value);
              }}
              onBlur={(e) => {
                handleBlur(e);
                setFieldValue("DescriptionAssetSold", values.DescriptionAssetSold.trim());
              }}
              isInvalid={Boolean(errors.DescriptionAssetSold) && touched.DescriptionAssetSold}
              errorMsg={errors.DescriptionAssetSold}
            />
          </FormField>
        ) : null}

        {values.TypeOfStock !== "Indian ESOP&RSU - Listed" && (
          <FormField label="Fair Market value as on date of sale" sm={4}>
            <NumericInputField
              showLeftIcon={true}
              placeholder=""
              name="FairMarketValue"
              value={values.FairMarketValue}
              onChange={(value) => {
                setFieldValue("FairMarketValue", value);
                getNetIncome(
                  values.TotalSalePrice,
                  value,
                  values.ExerciseValue,
                  values.TransferPrice
                );
              }}
              onBlur={handleBlur("FairMarketValue")}
              isInvalid={Boolean(errors.FairMarketValue) && touched.FairMarketValue}
              errorMsg={errors.FairMarketValue}
            />
          </FormField>
        )}

        <FormField label="Gains" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="Gains"
            value={values.Gains}
            onChange={(value) => setFieldValue("Gains", value)}
            onBlur={handleBlur("Gains")}
            isInvalid={Boolean(errors.Gains) && touched.Gains}
            errorMsg={errors.Gains}
            isDisabled={true}
            allowNegative={true}
          />
        </FormField>
      </section>
    </>
  );
};

export default CGStockOptionsRSUsForm;
